<template>
  <div class="sm:py-12">
    <div class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
      <form @submit.prevent="sendNotification" class="p-6 space-y-6">
        <!-- Target User Group -->
        <div>
          <label for="userGroup" class="block text-sm font-medium text-gray-700 dark:text-gray-200">Send to</label>
          <select
            id="userGroup"
            v-model="form.userGroup"
            class="block py-2 pr-10 pl-3 mt-1 w-full text-base rounded-md border border-gray-300 shadow-sm dark:text-gray-200 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            required
          >
            <option value="">Select a user group</option>
            <option value="customers">All Customers</option>
            <option value="businesses">All Businesses</option>
            <option value="drivers">All Drivers</option>
            <option value="all">All Users</option>
          </select>
        </div>

        <!-- Notification Type -->
        <div>
          <label for="notificationType" class="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >Notification Type</label
          >
          <select
            id="notificationType"
            v-model="form.type"
            class="block py-2 pr-10 pl-3 mt-1 w-full text-base rounded-md border border-gray-300 shadow-sm dark:text-gray-200 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            required
          >
            <option value="">Select a notification type</option>
            <option value="info">Information</option>
            <option value="promo">Promotion</option>
            <option value="update">System Update</option>
            <option value="alert">Alert</option>
          </select>
        </div>

        <!-- Notification Title -->
        <div>
          <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-200">Title</label>
          <input
            type="text"
            id="title"
            v-model="form.title"
            class="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 shadow-sm dark:placeholder:text-gray-200 dark:text-gray-200 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Notification title"
            required
          />
        </div>

        <!-- Notification Message -->
        <div>
          <label for="message" class="block text-sm font-medium text-gray-700 dark:text-gray-200">Message</label>
          <textarea
            id="message"
            v-model="form.message"
            rows="4"
            class="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 shadow-sm dark:placeholder:text-gray-200 dark:text-gray-200 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Notification message"
            required
          ></textarea>
        </div>

        <!-- Action URL (Optional) -->
        <div>
          <label for="actionUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Action URL (Optional)
          </label>
          <input
            type="text"
            id="actionUrl"
            v-model="form.actionUrl"
            class="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 shadow-sm dark:placeholder:text-gray-200 dark:text-gray-200 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="https://example.com/action"
          />
        </div>

        <!-- Action Text (Optional) -->
        <div>
          <label for="actionText" class="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Action Text (Optional)
          </label>
          <input
            type="text"
            id="actionText"
            v-model="form.actionText"
            class="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 shadow-sm dark:placeholder:text-gray-200 dark:text-gray-200 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="View Details"
          />
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end">
          <button
            type="submit"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md border border-transparent shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            :disabled="loading"
          >
            <span v-if="loading" class="mr-2">
              <svg
                class="w-4 h-4 text-white animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </span>
            {{ loading ? 'Sending...' : 'Send Notification' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useAdminStore } from '@/stores/adminStore'
import { useToast } from 'primevue/usetoast'
const toast = useToast()
const adminStore = useAdminStore()
const loading = ref(false)
const alertMessage = ref('')
const alertType = ref('success')

const form = reactive({
  userGroup: '',
  type: '',
  title: '',
  message: '',
  actionUrl: '',
  actionText: '',
})

const sendNotification = async () => {
  loading.value = true
  alertMessage.value = ''

  try {
    await adminStore.sendBulkNotification({
      userGroup: form.userGroup,
      type: form.type,
      title: form.title,
      message: form.message,
      action_url: form.actionUrl || null,
      action_text: form.actionText || null,
    })

    // Reset form after successful submission
    form.userGroup = ''
    form.type = ''
    form.title = ''
    form.message = ''
    form.actionUrl = ''
    form.actionText = ''

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Notifications sent successfully!',
      life: 3000,
    })
  } catch (error) {
    console.error('Error sending notifications:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Failed to send notifications. Please try again.',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}
</script>
